import { z } from "zod";
export const SHOP_VERSION = "currency-20261029-v3";
// A price revision does not reset the existing once-per-window entitlement.
const SHOP_OFFER_FAMILY = "currency-20261027-v2";
export const SHOP_STARTS_AT = Date.UTC(2026, 9, 27);
export const SHOP_OFFER_WINDOW_MS = 6 * 60 * 60 * 1000;
export const SHOP_IDS = [
  "credits-1",
  "credits-5",
  "credits-10",
  "launch-currency",
] as const;
const storedSkuSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,99}$/);
export const shopQuoteSchema = z.strictObject({
  format: z.literal(1),
  version: z.string().min(1).max(100),
  sku: storedSkuSchema,
  title: z.string().min(1).max(48),
  votes: z.number().int().positive(),
  credits: z.string().regex(/^[1-9]\d*$/),
  components: z.number().int().positive(),
  startsAt: z.number().int().nonnegative(),
  endsAt: z.number().int().nonnegative().nullable(),
  once: z.boolean(),
  offerId: storedSkuSchema.optional(),
  bonusCreditsPercent: z.number().int().nonnegative().max(1000).optional(),
  bonusComponentsPercent: z.number().int().nonnegative().max(1000).optional(),
});
export type ShopQuote = z.infer<typeof shopQuoteSchema>;
const shopOfferSchema = shopQuoteSchema.extend({ sku: z.enum(SHOP_IDS) });
export type ShopOffer = z.infer<typeof shopOfferSchema>;
/** Permanent pack definitions. Capture the timed catalog from authoritative server time. */
export const SHOP_CATALOG: readonly ShopOffer[] = [
  {
    format: 1,
    version: SHOP_VERSION,
    sku: "credits-1",
    title: "20 000 кредитов и 600 компонентов",
    votes: 1,
    credits: "20000",
    components: 600,
    startsAt: Date.UTC(2026, 9, 27),
    endsAt: null,
    once: false,
  },
  {
    format: 1,
    version: SHOP_VERSION,
    sku: "credits-5",
    title: "110 000 кредитов и 3 600 компонентов",
    votes: 5,
    credits: "110000",
    components: 3600,
    bonusCreditsPercent: 10,
    bonusComponentsPercent: 20,
    startsAt: Date.UTC(2026, 9, 27),
    endsAt: null,
    once: false,
  },
  {
    format: 1,
    version: SHOP_VERSION,
    sku: "credits-10",
    title: "240 000 кредитов и 7 800 компонентов",
    votes: 10,
    credits: "240000",
    components: 7800,
    bonusCreditsPercent: 20,
    bonusComponentsPercent: 30,
    startsAt: Date.UTC(2026, 9, 27),
    endsAt: null,
    once: false,
  },
];

const timedPacks = [
  { title: "96 000 кредитов и 2 700 компонентов", votes: 3, credits: "96000", components: 2700, bonusCreditsPercent: 60, bonusComponentsPercent: 50 },
  { title: "170 000 кредитов и 5 100 компонентов", votes: 5, credits: "170000", components: 5100, bonusCreditsPercent: 70, bonusComponentsPercent: 70 },
  { title: "288 000 кредитов и 8 160 компонентов", votes: 8, credits: "288000", components: 8160, bonusCreditsPercent: 80, bonusComponentsPercent: 70 },
];

/** A global window survives reloads, sessions and process restarts without a personal countdown. */
export function captureShopCatalog(now: number): { version: string; items: ShopOffer[] } {
  if (!Number.isSafeInteger(now) || now < 0) throw new Error("Invalid shop clock");
  const window = Math.max(0, Math.floor((now - SHOP_STARTS_AT) / SHOP_OFFER_WINDOW_MS));
  const startsAt = SHOP_STARTS_AT + window * SHOP_OFFER_WINDOW_MS;
  const endsAt = startsAt + SHOP_OFFER_WINDOW_MS;
  if (!Number.isSafeInteger(endsAt)) throw new Error("Shop clock exceeds supported range");
  const pack = timedPacks[window % timedPacks.length];
  if (!pack) throw new Error("Missing timed resource pack");
  const version = `${SHOP_VERSION}-w${window}`;
  const offer: ShopOffer = { ...pack, format: 1, version, sku: "launch-currency", startsAt, endsAt, once: true,
    offerId: `${SHOP_OFFER_FAMILY}-offer-${window}` };
  return { version: SHOP_VERSION, items: [...SHOP_CATALOG.map(item => ({ ...item })), offer] };
}
export const shopOrderInputSchema = z.strictObject({
  requestId: z.string().min(8).max(100),
  sku: storedSkuSchema,
  version: z.string().min(1).max(100),
});
export const shopOrderViewSchema = z.strictObject({
  id: z.uuid(),
  item: z.string().max(100),
  state: z.enum(["created", "charged", "refunded", "closed"]),
  testMode: z.boolean().nullable(),
  paymentStarted: z.boolean(),
  quote: shopQuoteSchema,
  expiresAt: z.number().int().nonnegative(),
});
export type ShopOrderView = z.infer<typeof shopOrderViewSchema>;
export const shopViewSchema = z.strictObject({
  enabled: z.boolean(),
  eligible: z.boolean(),
  serverNow: z.number().int(),
  version: z.string(),
  items: z
    .array(
      shopOfferSchema.extend({ available: z.boolean(), reason: z.string() }),
    )
    .max(4),
});
export type ShopView = z.infer<typeof shopViewSchema>;
